import React, { ReactElement } from 'react';
import { Column, useSafeIntl } from 'bluesquare-components';
import { ColorBadge } from 'Iaso/components/ColorBadge';
import DeleteDialog from '../../components/dialogs/DeleteDialogComponent';
import { ProjectChip } from '../projects/components/ProjectChip';
import { EditTeamModal } from './components/CreateEditTeam';
import { TypeCell } from './components/TypeCell';
import { UsersTeamsCell } from './components/UsersTeamsCell';
import { useDeleteTeam } from './hooks/requests/useDeleteTeam';
import { TeamList } from './hooks/requests/useGetTeams';
import MESSAGES from './messages';
import { TeamParams } from './types/team';

export const useTeamColumns = ({
    params,
    data,
}: {
    params: TeamParams;
    data?: TeamList;
}): Column[] => {
    const { mutate: deleteTeam } = useDeleteTeam({
        params,
        count: data?.count ?? 0,
    });
    const { formatMessage } = useSafeIntl();
    return [
        {
            Header: 'Id',
            accessor: 'id',
            width: 80,
        },
        {
            Header: formatMessage(MESSAGES.color),
            accessor: 'color',
            id: 'color',
            width: 10,
            sortable: false,
            Cell: settings => (
                <ColorBadge
                    data-testid={'team-badge-color'}
                    backgroundColor={settings.row.original.color}
                />
            ),
        },
        {
            Header: formatMessage(MESSAGES.name),
            accessor: 'name',
            id: 'name',
        },
        {
            Header: formatMessage(MESSAGES.project),
            accessor: 'project_details',
            id: 'project__name',
            Cell: settings => (
                <ProjectChip project={settings.row.original.project_details} />
            ),
        },
        {
            Header: formatMessage(MESSAGES.type),
            accessor: 'type',
            id: 'type',
            Cell: (settings): ReactElement => (
                <TypeCell type={settings.row.original.type} />
            ),
        },
        {
            Header: formatMessage(MESSAGES.usersTeams),
            accessor: 'users_details',
            sortable: false,
            Cell: (settings): ReactElement => (
                <UsersTeamsCell
                    type={settings.row.original.type}
                    subTeamsDetails={
                        settings.row.original.sub_teams_details || []
                    }
                    usersDetails={settings.row.original.users_details || []}
                />
            ),
        },
        {
            Header: formatMessage(MESSAGES.membersCount),
            accessor: 'members_count',
            id: 'members_count',
            sortable: true,
        },
        {
            Header: formatMessage(MESSAGES.actions),
            accessor: 'actions',
            resizable: false,
            sortable: false,
            Cell: (settings): ReactElement => {
                return (
                    // TODO: limit to user permissions
                    <>
                        <EditTeamModal
                            dialogType="edit"
                            id={settings.row.original.id}
                            iconProps={{}}
                        />
                        <DeleteDialog
                            keyName="team"
                            titleMessage={MESSAGES.delete}
                            onConfirm={() => deleteTeam(settings.row.original)}
                        />
                    </>
                );
            },
        },
    ];
};
